// shared/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { message, AutoCompleteProps } from 'antd';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'ttag';
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete';
import { ErrorMessageIcon } from './assets';
import { getAntdSize, getDataGoogle } from './helper';

let placesPromise: Promise<unknown> | null = null;

/**
 * Loads the Google Maps Places library once for address autocomplete. Sets the API key and places library on the first call, caches the import promise in module scope and returns that same promise on later calls.
 *
 * @param apiKey - Google Maps API key, used only by the first call.
 * @returns Promise resolving to the loaded Places library.
 */
const loadGooglePlaces = async (apiKey: string) => {
  if (!placesPromise) {
    setOptions({
      key: apiKey,
      libraries: ['places'],
    });
    placesPromise = importLibrary('places');
  }
  return placesPromise;
};

/**
 * Shows a global error toast for a failed operation. Suppresses content mentioning Network error, Invalid access token, 403, 503 or 400 - the last unless it also mentions Exceeded maximum of - and otherwise renders an antd error message with the custom error icon.
 *
 * @param content - Error message to display.
 * @returns Nothing.
 */
const showMessageError = ({ content }: any) => {
  if (
    !_.includes(content, 'Network error') &&
    !_.includes(content, 'Invalid access token') &&
    !_.includes(content, '403') &&
    !_.includes(content, '503') &&
    (!_.includes(content, '400') || _.includes(content, 'Exceeded maximum of'))
  ) {
    message.error({
      className: 'custom-message',
      icon: <img src={ErrorMessageIcon} alt="Error message icon" />,
      content,
    });
  }
};

type SetStateAction<S> = Partial<S> | ((prevState: S) => Partial<S> | null);

/**
 * Provides class-style partial updates for a single state object. Wraps useState with a memoised setter that merges a patch object, or the result of a patch function, into the previous state through lodash assign and keeps the previous state when a patch function returns nothing.
 *
 * @param initialState - Initial value of the state object.
 * @returns Tuple of the current state and the merging setter.
 */
const useSetState = <S extends object>(
  initialState: S,
): [S, (patch: SetStateAction<S>) => void] => {
  const [state, set] = useState<S>(initialState);
  /**
   * Merges a partial update into the state object, class-component style.
   * Applies a patch function to the previous state or takes a patch object, keeping the previous
   * state when the function returns nothing and otherwise assigning the patch onto a copy.
   *
   * @param patch - Partial state, or a function deriving one from the previous state.
   * @returns Nothing.
   */
  const setState = useCallback((patch: SetStateAction<S>) => {
    set((prevState) => {
      if (_.isFunction(patch)) {
        const data = patch(prevState);
        if (_.isNil(data)) {
          return prevState;
        }
        return _.assign({}, prevState, data);
      }
      return _.assign({}, prevState, patch);
    });
  }, []);
  return [state, setState];
};

/**
 * Runs an effect only when its dependencies change after the first render, never on mount.
 * Uses a ref to skip the initial effect run, calls effect on later runs, and returns the optional
 * cleanup from every run.
 *
 * @param effect - Effect to run on dependency updates.
 * @param dependencies - Values whose changes trigger the effect.
 * @param cleanup - Optional cleanup returned from the effect.
 * @returns Nothing.
 */
function useUpdateEffect(effect: (...args: any[]) => any, dependencies: any[] = [], cleanup?: any) {
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      effect();
    }
    return cleanup;
  }, dependencies);
}

interface IBaseProps {
  /** Class name of the component  */
  className?: string;
}

interface LabeledValue {
  key?: string;
  value: string | number;
  label: React.ReactNode;
}

type ValueSelect =
  | string
  | string[]
  | number
  | number[]
  | LabeledValue
  | LabeledValue[]
  | undefined
  | null
  | any;

interface IAddressValue {
  value?: number | string;
  label?: string;
  placeId?: string;
  address?: string;
}

interface IProps extends IBaseProps, AutoCompleteProps {
  /** The select title */
  title?: string;
  /** Whether this field is required */
  isRequired?: boolean;
  /** Country to search address */
  country?: string;
  /** Set validation status */
  isError?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Whether this field is search city */
  isSearchCity?: boolean;
  /** Called when input value change */
  onChangeInput?: (value: string, isSearchCity: boolean | undefined) => void;
  /** Called when select an option */
  onChangeAddress?: (value: any, isSearchCity: boolean | undefined) => void;
  /** Called when clear input */
  eventClear?: (isSearchCity: boolean | undefined) => void;
  /** Google Maps API key used to load the Places library */
  googleApiKey: string;
}

/**
 * Drives the Google Places address autocomplete, exposing suggestions, the current address and the change handlers. Lazily loads the Google Places script before initialising usePlacesAutocomplete with the country restriction and a cities-only type filter, mirrors the value prop into state, clears suggestions when the country changes, maps incoming predictions into label and placeId option lists, debounces the query by 500ms, and memoizes the Ant Design size.
 *
 * @param props - Country restriction, city-search flag, value, size and the change, input and clear callbacks.
 * @returns The autocomplete state, resolved Ant Design size, and the change and search handlers.
 */
export const useAutoComplete = (
  props: Pick<
    IProps,
    | 'country'
    | 'isSearchCity'
    | 'onChangeInput'
    | 'onChangeAddress'
    | 'eventClear'
    | 'value'
    | 'size'
    | 'googleApiKey'
  >,
) => {
  const countryRef = useRef<any>(undefined);
  const refCurrent = useRef<any>({});

  const [state, setState] = useSetState<{
    addressArray?: IAddressValue[];
    address?: string | ValueSelect;
  }>({
    addressArray: [],
    address: '',
  });

  const searchTimeoutRef = useRef<any | null>(null);

  const {
    suggestions: { data },
    setValue,
    init,
  } = usePlacesAutocomplete({
    initOnMount: false,
    requestOptions: {
      componentRestrictions: {
        country: props.country ? props.country : ['CA', 'US'],
      },
      types: props.isSearchCity ? ['(cities)'] : [],
    },
    cache: false,
  });

  useEffect(() => {
    (async () => {
      try {
        await loadGooglePlaces(props.googleApiKey);
        init();
      } catch (err) {
        console.error('Failed to load Google Places', err);
      }
    })();
  }, []);

  /**
   * Returns the address autocomplete to its empty state. Clears both the suggestion list and the current address text in component state.
   *
   * @returns Nothing.
   */
  const resetData = () => {
    setState({ addressArray: [], address: '' });
  };

  /**
   * Handles selection or clearing of an address suggestion. On selection it finds the cached prediction, fetches its place details, builds the flat address object with the UTC offset and either the street address or the city, and calls onChangeAddress, surfacing a geocode failure message on error; clearing instead resets state and fires the eventClear callback.
   *
   * @param valueNumber - Index of the selected option.
   * @param addressSelect - The chosen suggestion, or null when cleared.
   * @returns Nothing.
   */
  const onChange = (valueNumber: number, addressSelect: any) => {
    if (addressSelect) {
      /**
       * Computes the stored address entry matching the currently selected suggestion. Looks up the ref's cached address list for the item whose value equals the selected suggestion's value.
       *
       * @returns The matching cached address entry, or undefined.
       */
      const eleData = _.find(
        refCurrent.current.dataAddress,
        (x) => x.value === addressSelect.value,
      );
      if (eleData) {
        const { placeId } = eleData;
        if (placeId) {
          const parameter = {
            placeId,
          };
          getDetails(parameter)
            .then((result) => {
              if (result) {
                const {
                  address_components: addressComponents = {},
                  utc_offset_minutes: utcOffsetMinutes = 0,
                } = result;
                const object: any = getDataGoogle(addressComponents);
                _.assign(object, { utcOffset: utcOffsetMinutes || 0 });
                if (!props.isSearchCity) {
                  _.assign(object, { address: eleData.address });
                } else if (!object.city) {
                  _.assign(object, { city: eleData.address });
                }
                if (props.onChangeAddress) {
                  props.onChangeAddress(object, props.isSearchCity);
                }
              }
            })
            .catch(() => {
              showMessageError({ content: t`Failed to get geocode` });
            });
        }
        setState({ address: eleData.address });
      }
    } else {
      resetData();
      if (props.eventClear) {
        props.eventClear(props.isSearchCity);
      }
    }
  };

  /**
   * Handles a search query typed into the address autocomplete. Forwards the text and the city-search flag to the onChangeInput prop when present, then stores the text as the current address in component state.
   *
   * @param inputValue - Search text entered by the user.
   * @returns Nothing.
   */
  const handleSearch = (inputValue: string) => {
    if (props.onChangeInput) {
      props.onChangeInput(inputValue, props.isSearchCity);
    }
    setState({ address: inputValue });
  };

  useEffect(() => {
    if (props.value !== undefined) {
      setState({ address: props.value });
    }
  }, [props.value]);

  useEffect(() => {
    if (props.country !== countryRef.current) {
      setState({ addressArray: [] });
      countryRef.current = props.country;
    }
  }, [props.country]);

  useEffect(() => {
    const newArray: IAddressValue[] = [];
    const newArrayData: IAddressValue[] = [];
    _.forEach(data, (x, index) => {
      const {
        structured_formatting: { main_text: mainText, secondary_text: secondaryText },
        place_id: placeId,
      } = x;
      newArray.push({
        value: `${index}`,
        label: `${mainText}${secondaryText ? `, ${secondaryText}` : ''}`,
      });
      newArrayData.push({
        value: `${index}`,
        label: `${mainText}${secondaryText ? `, ${secondaryText}` : ''}`,
        placeId,
        address: mainText,
      });
    });
    refCurrent.current = { dataAddress: newArrayData };
    setState({ addressArray: newArray });
  }, [data]);

  useUpdateEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setValue(state.address);
    }, 500);
  }, [state.address]);

  /**
   * Computes the memoized Antd size derived from the component's size prop. Recomputes getAntdSize(props.size) only when props.size changes.
   *
   * @returns The memoized Antd size value.
   */
  const antSize = useMemo<ReturnType<typeof getAntdSize>>(
    () => getAntdSize(props.size),
    [props.size],
  );

  return {
    state,
    antSize: antSize as ReturnType<typeof getAntdSize>,
    onChange,
    handleSearch,
  };
};
